import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { RelatedNews } from "@/components/RelatedNews";
import { API, useAuth } from "@/App";
import {
  ArrowLeft, Trophy, MapPin, Calendar, Clock, Circle,
  Goal, Square, RectangleVertical, ArrowLeftRight, AlertTriangle, StickyNote,
  Users, Activity,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import MatchAdminConsole from "@/pages/MatchAdminConsole";
import LiveClock from "@/components/LiveClock";
import { formatMatchDate } from "@/lib/dates";

const EVENT_ICON = {
  goal: <Goal className="w-4 h-4 text-[#009E60]" />,
  own_goal: <Goal className="w-4 h-4 text-red-500" />,
  penalty_goal: <Goal className="w-4 h-4 text-[#009E60]" />,
  penalty_missed: <AlertTriangle className="w-4 h-4 text-orange-500" />,
  yellow_card: <RectangleVertical className="w-4 h-4 fill-[#FFC72C] text-[#FFC72C]" />,
  red_card: <RectangleVertical className="w-4 h-4 fill-red-500 text-red-500" />,
  substitution: <ArrowLeftRight className="w-4 h-4 text-blue-500" />,
  var: <Square className="w-4 h-4 text-purple-500" />,
  note: <StickyNote className="w-4 h-4 text-gray-500" />,
};

const EVENT_LABEL = {
  goal: "Goal",
  own_goal: "Own Goal",
  penalty_goal: "Penalty",
  penalty_missed: "Penalty Missed",
  yellow_card: "Yellow Card",
  red_card: "Red Card",
  substitution: "Substitution",
  var: "VAR",
  note: "Note",
};

const formatMinute = (m, added) => (added ? `${m}+${added}'` : `${m}'`);

const formatDate = (d) => {
  if (!d) return "TBD";
  return formatMatchDate(d, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }, "en-GB") || d;
};

const StatBar = ({ label, home, away }) => {
  const h = home ?? 0;
  const a = away ?? 0;
  const total = h + a;
  const hasData = home != null || away != null;
  const homePct = total > 0 ? (h / total) * 100 : 50;
  return (
    <div className="py-3">
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="font-heading font-bold w-8 text-left">{hasData ? h : "—"}</span>
        <span className="uppercase text-xs tracking-widest text-gray-500">{label}</span>
        <span className="font-heading font-bold w-8 text-right">{hasData ? a : "—"}</span>
      </div>
      <div className="flex gap-0.5 h-1.5">
        <div className="flex-1 bg-gray-100 relative overflow-hidden">
          <div
            className="absolute inset-y-0 right-0 bg-[#009E60]"
            style={{ width: hasData ? `${homePct}%` : 0 }}
          />
        </div>
        <div className="flex-1 bg-gray-100 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-[#FFC72C]"
            style={{ width: hasData ? `${100 - homePct}%` : 0 }}
          />
        </div>
      </div>
    </div>
  );
};

const TimelineEvent = ({ event, isHome }) => {
  const label = EVENT_LABEL[event.type] || event.type;
  const icon = EVENT_ICON[event.type] || <Circle className="w-4 h-4" />;
  return (
    <div className={`flex items-center gap-3 py-3 border-b border-gray-100 ${isHome ? "" : "flex-row-reverse text-right"}`} data-testid={`event-${event.event_id}`}>
      <div className="w-14 font-mono text-sm text-gray-500 shrink-0">{formatMinute(event.minute, event.added_time)}</div>
      <div className="w-6 h-6 shrink-0 flex items-center justify-center">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-heading font-semibold text-[#09090b] truncate">
          {event.player_name || "Unknown"}
        </div>
        <div className="text-xs text-gray-500">
          {label}
          {event.secondary_player_name && event.type === "substitution" && (
            <> → {event.secondary_player_name}</>
          )}
          {event.detail && ` · ${event.detail}`}
        </div>
      </div>
    </div>
  );
};

const LineupList = ({ lineup, team }) => {
  if (!lineup) {
    return (
      <div className="text-gray-400 italic text-sm py-6 text-center">
        Lineup not published yet.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-gray-100">
        <img
          src={team?.logo_url || "https://placehold.co/40x40/09090b/FFF?text=T"}
          alt=""
          className="w-8 h-8 object-contain"
        />
        <div>
          <p className="font-heading font-bold uppercase text-sm">{team?.name || "Team"}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {lineup.formation && <span className="font-mono">{lineup.formation}</span>}
            {lineup.coach_name && <span>· Coach: {lineup.coach_name}</span>}
          </div>
        </div>
      </div>

      <div>
        <h4 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Starting XI</h4>
        <ul className="space-y-1">
          {lineup.starters?.length ? lineup.starters.map((p, i) => (
            <li key={i} className="flex items-center gap-3 py-1.5 text-sm">
              <span className="w-7 font-mono text-gray-500 text-right">{p.number ?? "—"}</span>
              <span className="flex-1 font-medium">
                {p.player_name}
                {p.is_captain && <span className="ml-2 text-[10px] px-1 bg-[#FFC72C] text-black font-bold rounded-sm">C</span>}
              </span>
              {p.position && <Badge variant="outline" className="text-[10px]">{p.position}</Badge>}
            </li>
          )) : <li className="text-gray-400 italic text-sm">No starters recorded</li>}
        </ul>
      </div>

      {lineup.substitutes?.length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Substitutes</h4>
          <ul className="space-y-1">
            {lineup.substitutes.map((p, i) => (
              <li key={i} className="flex items-center gap-3 py-1.5 text-sm text-gray-600">
                <span className="w-7 font-mono text-gray-400 text-right">{p.number ?? "—"}</span>
                <span className="flex-1">{p.player_name}</span>
                {p.position && <Badge variant="outline" className="text-[10px]">{p.position}</Badge>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const MatchCentrePage = () => {
  const { matchId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/matches/${matchId}/centre`);
      setData(res.data);
    } catch (e) {
      console.error("Match centre load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll match-centre payload every 20s so events/lineups/stats stay fresh.
    // The live clock has its own 5s poll via <LiveClock>, so we don't need to refetch every 5s.
    const iv = setInterval(() => {
      const live = ["first_half", "second_half", "extra_time_first", "extra_time_second", "paused", "half_time"].includes(data?.clock?.clock_state);
      if (data?.match?.is_live || live) load();
    }, 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId, data?.match?.is_live, data?.clock?.clock_state]);

  const homeEvents = useMemo(() => (data?.events || []).filter((e) => e.team_id === data?.match?.home_team_id), [data]);
  const awayEvents = useMemo(() => (data?.events || []).filter((e) => e.team_id === data?.match?.away_team_id), [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 px-4">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!data?.match) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-500">Match Not Found</h2>
          <Link to="/matches" className="text-[#009E60] hover:underline mt-4 inline-block">
            Back to Matches
          </Link>
        </div>
      </div>
    );
  }

  const { match, home_team, away_team, competition, events, lineup_home, lineup_away, statistics, shootout } = data;
  const showScore = match.status === "completed" || match.status === "live" || match.home_score != null;
  const hasShootout = shootout && Array.isArray(shootout.kicks) && shootout.kicks.length > 0;

  const canEdit = user && (user.role === "admin" || user.role === "match_officer" || (user.role === "team_manager" && (user.team_id === match.home_team_id || user.team_id === match.away_team_id)));

  return (
    <div data-testid="match-centre-page" className="bg-gray-50 min-h-screen">
      {/* HERO */}
      <section className="bg-gradient-to-br from-[#09090b] via-[#0f0f14] to-[#1a1a24] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <Link to="/matches" className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-6 transition-colors" data-testid="link-back-matches">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Matches
          </Link>

          <div className="flex flex-wrap items-center gap-3 mb-6 text-xs uppercase tracking-widest text-gray-400">
            {competition && <span className="text-[#FFC72C] font-semibold">{competition.name}</span>}
            {match.matchday && <span>· Matchday {match.matchday}</span>}
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(match.match_date)}</span>
            {match.match_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {match.match_time}</span>}
            {match.stadium && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {match.stadium}</span>}
          </div>

          <div className="grid grid-cols-3 items-center gap-4 md:gap-8">
            {/* HOME */}
            <Link to={`/teams/${home_team?.team_id}`} className="flex flex-col items-center text-center group" data-testid="link-home-team">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-white/5 p-3 mb-3 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <img src={home_team?.logo_url || "https://placehold.co/150x150/09090b/FFF?text=H"} alt={home_team?.name} className="max-w-full max-h-full object-contain" />
              </div>
              <h2 className="font-heading font-black uppercase text-base md:text-2xl group-hover:text-[#FFC72C] transition-colors" data-testid="text-home-team-name">
                {home_team?.name || "Home"}
              </h2>
            </Link>

            {/* SCORE / STATUS */}
            <div className="flex flex-col items-center">
              {/* Server-authoritative live clock (replaces manual minute entry) */}
              {(data?.clock?.clock_state && !["pre_match", "full_time", "abandoned"].includes(data.clock.clock_state)) && (
                <div className="mb-2" data-testid="match-live-indicator">
                  <LiveClock
                    matchId={match.match_id}
                    initialClock={data?.clock}
                    onClockUpdate={(c) => setData((d) => (d ? { ...d, clock: c } : d))}
                  />
                </div>
              )}
              {data?.clock?.clock_state === "full_time" && (
                <Badge className="bg-white/10 text-white mb-2 hover:bg-white/10" data-testid="badge-ft">Full Time</Badge>
              )}
              {data?.clock?.clock_state === "abandoned" && (
                <Badge className="bg-red-500/20 text-red-300 mb-2 hover:bg-red-500/20">Abandoned</Badge>
              )}
              {/* Legacy badges for matches that never used the clock */}
              {!data?.clock?.clock_state && match.status === "completed" && (
                <Badge className="bg-white/10 text-white mb-2 hover:bg-white/10">Full Time</Badge>
              )}
              {!data?.clock?.clock_state && match.status === "scheduled" && !match.is_live && (
                <Badge className="bg-white/10 text-white mb-2 hover:bg-white/10">Scheduled</Badge>
              )}
              {match.status === "postponed" && (
                <Badge className="bg-orange-500/20 text-orange-300 mb-2 hover:bg-orange-500/20">Postponed</Badge>
              )}
              {match.status === "suspended" && (
                <Badge className="bg-amber-500/20 text-amber-300 mb-2 hover:bg-amber-500/20">Suspended</Badge>
              )}

              <div className="flex items-center gap-3 md:gap-6">
                <span className="font-heading font-black text-4xl md:text-6xl" data-testid="text-home-score">
                  {showScore ? (match.home_score ?? 0) : "—"}
                </span>
                <span className="text-gray-500 text-2xl md:text-3xl">:</span>
                <span className="font-heading font-black text-4xl md:text-6xl" data-testid="text-away-score">
                  {showScore ? (match.away_score ?? 0) : "—"}
                </span>
              </div>
              {hasShootout && (
                <div className="mt-2 text-[#FFC72C] font-heading font-bold text-sm md:text-base uppercase tracking-widest" data-testid="text-shootout-score">
                  ({shootout.home_score ?? 0}-{shootout.away_score ?? 0} pens)
                </div>
              )}
            </div>

            {/* AWAY */}
            <Link to={`/teams/${away_team?.team_id}`} className="flex flex-col items-center text-center group" data-testid="link-away-team">
              <div className="w-20 h-20 md:w-28 md:h-28 bg-white/5 p-3 mb-3 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                <img src={away_team?.logo_url || "https://placehold.co/150x150/09090b/FFF?text=A"} alt={away_team?.name} className="max-w-full max-h-full object-contain" />
              </div>
              <h2 className="font-heading font-black uppercase text-base md:text-2xl group-hover:text-[#FFC72C] transition-colors" data-testid="text-away-team-name">
                {away_team?.name || "Away"}
              </h2>
            </Link>
          </div>

          {canEdit && (
            <div className="mt-6" data-testid="admin-console-wrapper">
              <MatchAdminConsole data={data} onChange={load} />
            </div>
          )}
        </div>
      </section>

      {/* TABS */}
      <section className="py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <Tabs defaultValue="summary" className="w-full">
            <TabsList className="w-full justify-start border-b border-gray-200 mb-8 bg-transparent h-auto p-0 overflow-x-auto">
              {[
                { v: "summary", label: "Summary" },
                { v: "timeline", label: "Timeline" },
                { v: "lineups", label: "Lineups" },
                { v: "statistics", label: "Statistics" },
              ].map((t) => (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  className="px-5 md:px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent whitespace-nowrap"
                  data-testid={`tab-${t.v}`}
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* SUMMARY */}
            <TabsContent value="summary">
              {hasShootout && (
                <div className="bg-white p-6 border border-gray-100 mb-6" data-testid="shootout-summary">
                  <h3 className="font-heading text-xl font-bold uppercase mb-4 border-l-4 border-[#FFC72C] pl-3 flex items-center justify-between">
                    <span>Penalty Shootout</span>
                    <span className="text-2xl font-heading font-black text-[#FFC72C]">
                      {shootout.home_score ?? 0} – {shootout.away_score ?? 0}
                    </span>
                  </h3>
                  <div className="grid grid-cols-2 gap-4 md:gap-8">
                    {[match.home_team_id, match.away_team_id].map((tid, idx) => {
                      const teamKicks = shootout.kicks.filter((k) => k.team_id === tid);
                      const teamName = idx === 0 ? (home_team?.short_name || home_team?.name || "Home") : (away_team?.short_name || away_team?.name || "Away");
                      return (
                        <div key={tid} data-testid={`shootout-team-${idx === 0 ? "home" : "away"}`}>
                          <p className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2">{teamName}</p>
                          {teamKicks.length ? (
                            <ul className="space-y-1">
                              {teamKicks.map((k) => (
                                <li key={k.kick_id} className="flex items-center gap-2 text-sm py-1 border-b border-gray-100 last:border-0">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${k.scored ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}>
                                    {k.scored ? "✓" : "✗"}
                                  </span>
                                  <span className="text-gray-800 truncate">{k.player_name}</span>
                                  <span className="ml-auto text-xs text-gray-400 font-mono">#{k.order}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-gray-400 italic text-sm">No kicks taken</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white p-6 border border-gray-100">
                  <h3 className="font-heading text-xl font-bold uppercase mb-4 border-l-4 border-[#FFC72C] pl-3 flex items-center gap-2">
                    <Activity className="w-5 h-5" /> Key Moments
                  </h3>
                  {events.length ? (
                    <div className="grid grid-cols-2 divide-x divide-gray-100">
                      <div className="pr-4">
                        {homeEvents.length ? homeEvents.map((e) => (
                          <TimelineEvent key={e.event_id} event={e} isHome />
                        )) : <p className="text-gray-400 italic text-sm py-4">No events</p>}
                      </div>
                      <div className="pl-4">
                        {awayEvents.length ? awayEvents.map((e) => (
                          <TimelineEvent key={e.event_id} event={e} isHome={false} />
                        )) : <p className="text-gray-400 italic text-sm py-4">No events</p>}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400 italic text-sm py-4">No key moments recorded yet.</p>
                  )}
                </div>

                <div className="bg-white p-6 border border-gray-100">
                  <h3 className="font-heading text-xl font-bold uppercase mb-4 border-l-4 border-[#FFC72C] pl-3">
                    Match Info
                  </h3>
                  <dl className="text-sm space-y-3">
                    <div className="flex justify-between border-b border-gray-100 pb-2">
                      <dt className="text-gray-500">Date</dt>
                      <dd className="font-semibold">{formatDate(match.match_date)}</dd>
                    </div>
                    {match.match_time && (
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <dt className="text-gray-500">Kick-off</dt>
                        <dd className="font-semibold">{match.match_time}</dd>
                      </div>
                    )}
                    {match.stadium && (
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <dt className="text-gray-500">Stadium</dt>
                        <dd className="font-semibold text-right">{match.stadium}</dd>
                      </div>
                    )}
                    {match.referee && (
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <dt className="text-gray-500">Referee</dt>
                        <dd className="font-semibold">{match.referee}</dd>
                      </div>
                    )}
                    {match.attendance != null && (
                      <div className="flex justify-between border-b border-gray-100 pb-2">
                        <dt className="text-gray-500">Attendance</dt>
                        <dd className="font-semibold">{match.attendance.toLocaleString()}</dd>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <dt className="text-gray-500">Competition</dt>
                      <dd className="font-semibold text-right">{competition?.name || "—"}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </TabsContent>

            {/* TIMELINE */}
            <TabsContent value="timeline">
              <div className="bg-white p-6 border border-gray-100">
                <h3 className="font-heading text-xl font-bold uppercase mb-4 border-l-4 border-[#FFC72C] pl-3">
                  Match Timeline
                </h3>
                {events.length ? (
                  <ol className="relative">
                    {events.map((e) => {
                      const isHome = e.team_id === match.home_team_id;
                      return (
                        <li key={e.event_id} className={`flex ${isHome ? "justify-start" : "justify-end"} mb-1`}>
                          <div className={`max-w-[85%] md:max-w-[60%] flex items-center gap-3 ${isHome ? "flex-row" : "flex-row-reverse"} bg-gray-50 px-4 py-2 border-l-4 ${isHome ? "border-l-[#009E60]" : "border-l-[#FFC72C]"}`}>
                            <span className="font-mono text-xs text-gray-500 w-12 shrink-0 text-center">{formatMinute(e.minute, e.added_time)}</span>
                            <span className="w-5 h-5 shrink-0">{EVENT_ICON[e.type]}</span>
                            <div className="min-w-0">
                              <div className="text-sm font-heading font-semibold truncate">{e.player_name || "Unknown"}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {EVENT_LABEL[e.type]}
                                {e.secondary_player_name && e.type === "substitution" && <> → {e.secondary_player_name}</>}
                                {e.detail && ` · ${e.detail}`}
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                ) : (
                  <p className="text-gray-400 italic text-sm">No events recorded yet.</p>
                )}
              </div>
            </TabsContent>

            {/* LINEUPS */}
            <TabsContent value="lineups">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-6 border border-gray-100" data-testid="lineup-home">
                  <LineupList lineup={lineup_home} team={home_team} />
                </div>
                <div className="bg-white p-6 border border-gray-100" data-testid="lineup-away">
                  <LineupList lineup={lineup_away} team={away_team} />
                </div>
              </div>
            </TabsContent>

            {/* STATISTICS */}
            <TabsContent value="statistics">
              <div className="bg-white p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <img src={home_team?.logo_url || "https://placehold.co/40x40/09090b/FFF?text=H"} alt="" className="w-8 h-8 object-contain" />
                    <span className="font-heading font-bold uppercase text-sm">{home_team?.name}</span>
                  </div>
                  <span className="font-heading text-xs uppercase tracking-widest text-gray-500">vs</span>
                  <div className="flex items-center gap-3">
                    <span className="font-heading font-bold uppercase text-sm">{away_team?.name}</span>
                    <img src={away_team?.logo_url || "https://placehold.co/40x40/09090b/FFF?text=A"} alt="" className="w-8 h-8 object-contain" />
                  </div>
                </div>

                {statistics ? (
                  <div className="space-y-1 divide-y divide-gray-100">
                    <StatBar label="Possession %" home={statistics.possession_home} away={statistics.possession_away} />
                    <StatBar label="Shots" home={statistics.shots_home} away={statistics.shots_away} />
                    <StatBar label="Shots on Target" home={statistics.shots_on_target_home} away={statistics.shots_on_target_away} />
                    <StatBar label="Corners" home={statistics.corners_home} away={statistics.corners_away} />
                    <StatBar label="Fouls" home={statistics.fouls_home} away={statistics.fouls_away} />
                    <StatBar label="Yellow Cards" home={statistics.yellow_cards_home} away={statistics.yellow_cards_away} />
                    <StatBar label="Red Cards" home={statistics.red_cards_home} away={statistics.red_cards_away} />
                    <StatBar label="Offsides" home={statistics.offsides_home} away={statistics.offsides_away} />
                    <StatBar label="Saves" home={statistics.saves_home} away={statistics.saves_away} />
                  </div>
                ) : (
                  <p className="text-gray-400 italic text-sm py-6 text-center">
                    Match statistics have not been published yet.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      <RelatedNews entity="match_id" id={matchId} title="Match News" />
    </div>
  );
};

export default MatchCentrePage;
